import { apiClient } from '../axiosInstance';
import { ENDPOINTS } from '../endpoints';
import type { Customer, CustomerAddress } from '../../types/customer';
import type {
  CustomerService,
  CreateCustomerPayload,
  AddressPayload,
  AddressSaveResult,
} from './interfaces/CustomerService';

// The v2 customer-addresses endpoints respond with the full updated customer,
// not the saved address. Pull the saved address out of `customer.addresses`
// (callers select it) and surface the canonical list so callers replace their
// local copy — reflecting server side effects like demoting the prior primary.
// Falls back to treating the response as a bare address (no list) when it
// carries no `addresses` array (older/other API shapes).
function toSaveResult(
  data: Customer | CustomerAddress,
  opts: { payload: AddressPayload; addressId?: number },
): AddressSaveResult {
  const list = (data as Customer).addresses;
  if (!Array.isArray(list) || list.length === 0) {
    return { address: data as CustomerAddress };
  }
  // Update: the saved row keeps its id, so match on it directly.
  let address: CustomerAddress | undefined;
  if (opts.addressId != null) {
    address = list.find((a) => a.id === opts.addressId);
  }
  if (!address) {
    // Create (or update fallback): match the row we just sent, then take the
    // newest (largest auto-increment id) in case the address already existed.
    const { street, city, state, zip_code } = opts.payload;
    const matches = list.filter(
      (a) =>
        a.street === street &&
        a.zip_code === zip_code &&
        a.city === city &&
        a.state === state,
    );
    const pool = matches.length ? matches : list;
    // Seed the reduce (WR-05): a seedless reduce throws on an empty pool. The
    // line-22 guard keeps `pool` non-empty today, but the seeded form is robust
    // against a future loosening of that guard and reads as an explicit max-by-id.
    address =
      pool.reduce<CustomerAddress | undefined>(
        (best, a) => (best == null || a.id > best.id ? a : best),
        undefined,
      ) ?? pool[0];
  }
  return { address, addresses: list };
}

export const customerService: CustomerService = {
  async updatePhones(userId, phones) {
    const { data } = await apiClient.patch<Customer>(
      ENDPOINTS.v1.users.update(userId),
      {
        user: { phones },
      },
    );
    return data;
  },

  async updateEmails(userId, emails) {
    const { data } = await apiClient.patch<Customer>(
      ENDPOINTS.v1.users.update(userId),
      {
        user: { emails },
      },
    );
    return data;
  },

  // Phase 6.2 (PROF-01) — atomic contact update. ONE PATCH /v1/users/:id
  // carrying name + phones + emails in a single `user` payload; the server
  // renders the full customer (_customer.json.jbuilder). Phone key is `type`,
  // NOT phone_type (Pitfall 4). New rows omit id.
  async updateContact(userId, contact) {
    const { data } = await apiClient.patch<Customer>(
      ENDPOINTS.v1.users.update(userId),
      {
        user: {
          first_name: contact.first_name,
          last_name: contact.last_name,
          phones: contact.phones,
          emails: contact.emails,
        },
      },
    );
    return data;
  },

  // Quick 260622-itv (PROF-02) — remove an address via the REAL destroy route
  // `DELETE /v1/addresses/:addressId`. This SUPERSEDES 06.2 D-10's PATCH-resend
  // strategy, which was a silent no-op: Rails `accepts_nested_attributes_for`
  // never deletes OMITTED nested rows, so the server returned 200 with the row
  // still present and the address reappeared. The destroy response is an empty
  // `{}` (NOT the full customer), so this resolves VOID — the caller updates the
  // auth store by dropping the removed row locally (D-05), it does NOT setUser a
  // server payload. `userId` is retained for call-site shape / logging; the
  // route is not user-scoped (ownership enforced server-side). Caller D-10
  // guards (block primary / last address) still run BEFORE this call.
  async deleteAddress(_userId, addressId) {
    await apiClient.delete(ENDPOINTS.v1.addresses.delete(addressId));
  },

  // Phase 6.2 (PROF-06) — POST the session-wrapped credentials to the Devise
  // change-password endpoint; resolve VOID on 200. The
  // `{ response: 'Password Changed' }` body is intentionally discarded — it is
  // NOT a customer and must NOT touch the auth store (Pitfall 5). A 422
  // rejection propagates so the caller surfaces `.message`.
  async changePassword(currentPassword, password, passwordConfirmation) {
    await apiClient.post(ENDPOINTS.v1.users.changePassword, {
      session: {
        current_password: currentPassword,
        password,
        password_confirmation: passwordConfirmation,
      },
    });
  },

  async createAddress(userId, address) {
    const { data } = await apiClient.post<Customer | CustomerAddress>(
      ENDPOINTS.v2.customers.addresses(userId),
      { address },
    );
    return toSaveResult(data, { payload: address });
  },

  async updateAddress(userId, addressId, address) {
    const { data } = await apiClient.put<Customer | CustomerAddress>(
      ENDPOINTS.v2.customers.address(userId, addressId),
      { address },
    );
    return toSaveResult(data, { payload: address, addressId });
  },

  async validateEmail(email) {
    await apiClient.get(ENDPOINTS.v1.users.validateEmail, {
      params: { email },
    });
  },

  async validateCoverageArea(zipCode, email) {
    await apiClient.get(ENDPOINTS.v1.users.validateCoverageArea, {
      params: { zip_code: zipCode, ...(email ? { email } : {}) },
    });
  },

  async siteverify(recaptchaToken) {
    await apiClient.post(ENDPOINTS.v1.users.siteverify, {
      token: recaptchaToken,
    });
  },

  async create(payload: CreateCustomerPayload) {
    const { data } = await apiClient.post<Customer>(ENDPOINTS.v1.users.create, {
      user: payload,
    });
    return data;
  },

  async update(userId, payload) {
    await apiClient.patch(ENDPOINTS.v1.users.update(userId), payload);
  },

  // DELETE the server-provided RELATIVE payment_method_delete_url verbatim — no
  // endpoint key, no route building. The id rides inside the URL the server
  // itself minted on the customer payload; ownership is enforced server-side
  // (../api v2/payment_methods_controller.rb:36 `authorize! :destroy`).
  async deletePaymentMethod(deleteUrl) {
    await apiClient.delete(deleteUrl);
  },

  // PODZ-1812 — standalone-save a tokenized payment method against the customer.
  // POST the server-minted `Customer.payment_method_create_url` VERBATIM (no
  // endpoint key, no route building — same contract as deletePaymentMethod above;
  // threat T-1812-01). The create endpoint returns the full server-rendered
  // customer, which we resolve so the caller can refresh the on-file method. NO
  // invoice, NO charge, NO `card_save_payment_method` (the modal builds the body;
  // saving IS the operation — legacy parity). A 422 propagates unchanged.
  async createPaymentMethod(createUrl, body) {
    const { data } = await apiClient.post<Customer>(createUrl, body);
    return data;
  },
};
